import React from 'react'
import EventSelectionCheckbox from '../../../components/EventSelectionCheckbox'
import CustomNoData from '../../../components/CustomNoData'
import PaginationV2 from '../../../components/pagination_v2'
import { FaPlus, FaArrowDown, FaArrowUp, FaMinus } from 'react-icons/fa'

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
})

const formatOdds = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'N/A'
  }
  return numberFormatter.format(Number(value))
}

const formatHandicap = (value) => {
  if (value === null || value === undefined) {
    return 'N/A'
  }
  return value
}

const formatKickoff = (value) => {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.toLocaleDateString()} | ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

const statusBadge = (status) => {
  switch (status) {
    case 'live':
      return 'bg-customOrange/20 text-customOrange'
    case 'final':
      return 'bg-customMuted/20 text-customMuted'
    default:
      return 'bg-customVibrantGreen/20 text-customVibrantGreen'
  }
}

const TrendIndicator = ({ trend, risingLabel, droppingLabel, stableLabel }) => {
  const normalised = typeof trend === 'string' ? trend.toLowerCase() : 'stable'
  let icon = <FaMinus className="text-customMuted" />
  let label = stableLabel

  if (normalised === 'rising') {
    icon = <FaArrowUp className="text-customAccent" />
    label = risingLabel
  } else if (normalised === 'dropping') {
    icon = <FaArrowDown className="text-customOrange" />
    label = droppingLabel
  }

  return (
    <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-customMuted">
      {icon}
      <span className={`${normalised === 'rising' ? 'text-customAccent' : normalised === "dropping" ? 'text-customOrange' : 'text-customMuted'}`}>{label}</span>
    </span>
  )
}

const EventBufferTable = ({
  events = [],
  selectionMap = {},
  onToggleSelection,
  onManualUpdate,
  onManualSave,
  rowFetchLoading = {},
  rowSaveLoading = {},
  pagination = null,
  lastSyncedAt = null,
  actionsDisabled = false,
}) => {
  if (!events.length) {
    return (
      <div className="overflow-hidden rounded-3xl border border-customAccent/20 bg-white/90 shadow-xl shadow-customAccent/5 dark:border-customSurfaceMuted dark:bg-customSurface">
        <div className="flex min-h-[320px] items-center justify-center p-6 text-center">
          <CustomNoData
            title="Buffer is clear"
            description="No buffered events match your filters yet."
          />
        </div>
        {pagination ? (
          <PaginationV2
            startIndex={pagination.startIndex}
            endIndex={pagination.endIndex}
            totalItems={pagination.totalItems}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            itemLabel="buffered events"
          />
        ) : null}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-customAccent/20 bg-white/90 shadow-xl shadow-customAccent/5 dark:border-customSurfaceMuted dark:bg-customSurface">
      <div className="hidden min-w-full overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-customMuted/40 dark:divide-customSurfaceMuted">
          <thead className="bg-background text-xs uppercase tracking-wide text-customLightMilkWhite dark:bg-custom_dark_secondary/60 dark:text-customMuted">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Event</th>
              <th className="px-4 py-2 text-left font-semibold">Odds</th>
              <th className="px-4 py-2 text-left font-semibold">Handicap</th>
              <th className="px-4 py-2 text-left font-semibold">Goal line</th>
              {/* <th className="px-4 py-3 text-left font-semibold">Flags</th> */}
              <th className="px-2 py-2 text-left font-semibold">Selection</th>
              <th className="px-4 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-customMuted/20 text-sm dark:divide-customSurfaceMuted/70">
            {events.map((event) => {
              const eventKey = String(event.id)
              const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
              const handicapLine = event.handicap?.line ?? event.handicap ?? 'N/A'
              const rowFetchBusy = Boolean(rowFetchLoading[eventKey])
              const rowSaveBusy = Boolean(rowSaveLoading[eventKey])
              const rowBusy = rowFetchBusy || rowSaveBusy
              const rowActionsDisabled = rowBusy || actionsDisabled

              return (
                <tr key={event.id} className={`${event.isDuplicate ? 'border-l-[3px] border-customAltas' : null} transition hover:bg-customSlate/40 dark:hover:bg-customSurfaceMuted/40`}>
                  <td className="min-w-[250px] px-4 py-2 align-top">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-customInk dark:text-customHeading">
                        <span className={`${event.homeTeamId === event.overTeamId ? 'text-customFontRed' : null}`}>{event.homeTeam}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-customMuted">
                          vs
                        </span>
                        <span className={`${event.awayTeamId === event.overTeamId ? 'text-customFontRed' : null}`}>{event.awayTeam}</span>
                      </div>
                      <div className="text-xs text-customSecondaryText dark:text-customMuted">
                        {event.leagueName}
                        {event.leagueCountry ? ` | ${event.leagueCountry}` : ''}
                      </div>
                      <div className="text-xs text-customSecondaryText dark:text-customMuted">
                        {formatKickoff(event.kickoff)} | <span className='text-customAccent dark:text-blue'>{eventKey}</span> | {event.isDuplicate ? <span className="text-customFontRed">Duplicate</span> : <span className="text-customVibrantGreen">Unique</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex flex-col gap-1 items-center justify-center">
                      <span className="text-xs font-medium text-customAccent bg-customAccent/10 dark:bg-customAccent/80 dark:text-customHeading px-2 py-0.5 rounded-full">
                        Body {event.burmeseBody}
                      </span>
                      <span className="text-xs font-medium text-customVibrantGreen bg-customVibrantGreen/10 dark:bg-customVibrantGreen/80 dark:text-customHeading px-2 py-0.5 rounded-full">
                        Goal {event.burmeseGoal}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-customInk dark:text-customHeading">
                        {handicapLine}
                      </span>
                      <div className="flex flex-col text-xs text-customSecondaryText dark:text-customMuted">
                        <span> H  {' | '} {formatOdds(event.homeOdds)}</span>
                        <span> A  {' | '} {formatOdds(event.awayOdds)}</span>
                      </div>
                      {/* <span className="text-xs text-customSecondaryText dark:text-customMuted">
                        H {formatHandicap(event.handicap?.home ?? event.handicap?.line)}
                        {' | '}
                        A {formatHandicap(event.handicap?.away ?? event.handicap?.line * -1)}
                      </span> 
                      <span className='flex gap-1 text-xs font-medium px-2 py-0.5 bg-background/5 text-background rounded-full dark:text-customWhite'>
                       {event.burmeseBody} 
                      <TrendIndicator
                        trend={event.handicapTrend}
                        risingLabel="Rising"
                        droppingLabel="Dropping"
                        stableLabel="Stable"
                      />
                      </span> */}
                    </div>
                  </td>
                  {/* <td className="w-28 px-4 py-2 align-top">
                    <div className="flex flex-col gap-1 text-sm">
                      <span className="flex items-center gap-2 text-customInk dark:text-customHeading">
                        <span className="rounded-full bg-customAccent/10 px-2 py-0.5 text-[11px] uppercase text-customAccent dark:bg-customAccent/20 dark:text-customHeading">
                          Home
                        </span>
                        {formatOdds(event.homeOdds)}
                      </span>
                      <span className="flex items-center gap-2 text-customInk dark:text-customHeading">
                        <span className="rounded-full bg-customVibrantGreen/10 px-2 py-0.5 text-[11px] uppercase text-customVibrantGreen dark:bg-customVibrantGreen/20 dark:text-customHeading">
                          Away
                        </span>
                        {formatOdds(event.awayOdds)}
                      </span>
                      <span className="text-xs text-customSecondaryText dark:text-customMuted">
                        Delta {formatOdds(event.oddsDelta)}
                      </span> 
                    </div>
                  </td> */}
                  <td className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1 text-sm text-customInk dark:text-customHeading">
                      <span className="font-semibold">O/U {formatHandicap(event.goalLine)}</span>
                      <span className="text-xs text-customSecondaryText dark:text-customMuted">
                        Over {formatOdds(event.goalOverOdds)}
                      </span>
                      <span className="text-xs text-customSecondaryText dark:text-customMuted">
                        Under {formatOdds(event.goalUnderOdds)}
                      </span>
                      {/* <span className='flex gap-1 font-medium text-xs px-2 py-0.5 bg-background/5 text-background rounded-full dark:text-customWhite'>
                      {event.burmeseGoal}
                      <TrendIndicator
                        trend={event.goalLineTrend}
                        risingLabel="Rising"
                        droppingLabel="Dropping"
                        stableLabel="Stable"
                      />
                      </span>  */}
                    </div>
                  </td>
                  {/* <td className="w-36 px-4 py-3 align-top">
                    <div className="flex flex-col gap-2 text-xs text-customSecondaryText dark:text-customMuted">
                      <span
                        className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${statusBadge(event.status)}`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {event.status ?? 'scheduled'}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide">
                        Updated {formatKickoff(event.lastSyncedAt)}
                      </span>
                    </div>
                  </td> */}
                  <td className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1">
                      <EventSelectionCheckbox
                        type="single"
                        tone="single"
                        label="Single"
                        checked={Boolean(selection.single)}
                        disabled={!event.singleEligible}
                        //description={event.singleEligible ? 'For straight bet' : 'Not eligible'}
                        onChange={(checked) => onToggleSelection?.(event.id, 'single', checked)}
                      />
                      <EventSelectionCheckbox
                        type="parlay"
                        tone="parlay"
                        label="Parlay"
                        checked={Boolean(selection.parlay)}
                        disabled={!event.parlayEligible}
                        //description={event.parlayEligible ? 'Add to parlay mix' : 'Not eligible'}
                        onChange={(checked) => onToggleSelection?.(event.id, 'parlay', checked)}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className='flex flex-col gap-1'>
                      <button
                        type="button"
                        onClick={() => onManualUpdate?.(event)}
                        disabled={rowActionsDisabled}
                        className="flex items-center gap-1 rounded-xl border border-customAccent/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-customAccent/40 dark:text-customHeading"
                      >
                        {rowFetchBusy ? (
                          <>
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            Fetching...
                          </>
                        ) : (
                          <>
                            <span aria-hidden="true">↻</span>
                            Manual Fetch
                            {/* Fetch */}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => onManualSave?.(event)}
                        disabled={rowActionsDisabled}
                        className="flex items-center gap-1 rounded-xl border border-customAccent/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-customAccent/40 dark:text-customHeading"
                      >
                        {rowSaveBusy ? (
                          <>
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            Adding ...
                          </>
                        ) : (
                          <>
                            <span aria-hidden="true"><FaPlus /></span>
                            Manual Add
                            {/* Add */}
                          </>
                        )}
                      </button>
                      {
                        lastSyncedAt && lastSyncedAt[event.id] !== undefined ?
                        <span className="flex gap-1 items-center text-[9px] uppercase tracking-wide text-customSecondaryText/80 dark:text-customSky/60 font-bold">
                          <span className="h-2 w-2 rounded-full bg-customSecondaryText/80 dark:bg-customSky/60" />
                          Last Added {formatRelative(new Date(lastSyncedAt[event.id]))}
                        </span> : null
                      }
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="grid gap-4 p-4 lg:hidden">
        {events.map((event) => {
          const eventKey = String(event.id)
          const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
          const rowFetchBusy = Boolean(rowFetchLoading[eventKey])
          const rowSaveBusy = Boolean(rowSaveLoading[eventKey])
          const rowBusy = rowFetchBusy || rowSaveBusy
          const rowActionsDisabled = rowBusy || actionsDisabled
          const handicapLine = event.handicap?.line ?? event.handicap ?? 'N/A'
          return (
            <article
              key={event.id}
              className="rounded-2xl border border-customAccent/20 bg-white/90 p-4 shadow-md shadow-customAccent/10 transition hover:border-customAccent/40 dark:border-customSurfaceMuted dark:bg-customSurface"
            >
              <header className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-customInk dark:text-customHeading">
                    {event.homeTeam} vs {event.awayTeam}
                  </h3>
                  <p className="text-xs text-customSecondaryText dark:text-customMuted">
                    {event.leagueName}
                    {event.leagueCountry ? ` | ${event.leagueCountry}` : ''}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${statusBadge(event.status)}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {event.status ?? 'scheduled'}
                </span>
              </header>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-xs text-customSecondaryText dark:text-customMuted">
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-customMuted">Kick-off</dt>
                  <dd className="text-sm text-customInk dark:text-customHeading">
                    {formatKickoff(event.kickoff)}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-customMuted">Handicap</dt>
                  <dd className="text-sm text-customInk dark:text-customHeading">{handicapLine}</dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-customMuted">Odds</dt>
                  <dd>
                    Home {formatOdds(event.homeOdds)} / Away {formatOdds(event.awayOdds)}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-customMuted">Goal line</dt>
                  <dd>
                    O/U {formatHandicap(event.goalLine)} (O {formatOdds(event.goalOverOdds)} / U{' '}
                    {formatOdds(event.goalUnderOdds)})
                  </dd>
                </div>
              </dl>
              <div className="mt-4 grid gap-2">
                <EventSelectionCheckbox
                  type="single"
                  tone="single"
                  label="Single"
                  checked={Boolean(selection.single)}
                  disabled={!event.singleEligible}
                  description={event.singleEligible ? 'For straight bet queue' : 'Not eligible'}
                  onChange={(checked) => onToggleSelection?.(event.id, 'single', checked)}
                />
                <EventSelectionCheckbox
                  type="parlay"
                  tone="parlay"
                  label="Parlay"
                  checked={Boolean(selection.parlay)}
                  disabled={!event.parlayEligible}
                  description={event.parlayEligible ? 'Add to parlay mix' : 'Not eligible'}
                  onChange={(checked) => onToggleSelection?.(event.id, 'parlay', checked)}
                />
              </div>
              <div className='flex gap-2'>
                <button
                  type="button"
                  onClick={() => onManualUpdate?.(event)}
                  disabled={rowActionsDisabled}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-customAccent/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-customAccent/40 dark:text-customHeading"
                >
                  {rowFetchBusy ? (
                    <>
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true">↻</span>
                      Manual fetch
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onManualSave?.(event)}
                  disabled={rowActionsDisabled}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-customAccent/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-customAccent/40 dark:text-customHeading"
                >
                  {rowSaveBusy ? (
                    <>
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true"><FaPlus /></span>
                      Manual add
                    </>
                  )}
                </button>
              </div>
            </article>
          )
        })}
      </div>
      {pagination ? (
        <PaginationV2
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          totalItems={pagination.totalItems}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={pagination.onPageChange}
          itemLabel="buffered events"
        />
      ) : null}
    </div>
  )
}

export default EventBufferTable;

const formatRelative = (timestamp) => {
  if (!timestamp) return 'Never'
  const value = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  if (Number.isNaN(value?.getTime?.())) return 'Unknown'

  const diffMs = Date.now() - value.getTime()
  if (diffMs < 0) return 'Just now'
  const diffMinutes = Math.floor(diffMs / 1000 / 60)
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
}
